import React from 'react';
import { ErrorBoundary } from './generalErrorBoundary';
import { RouteErrorBoundary } from './routeErrorBoundary';

// Error boundary configuration interface
export interface ErrorBoundaryConfig {
  // Global error handling
  enableGlobalErrorReporting?: boolean;
  errorReportingEndpoint?: string;
  
  // Error boundary behavior
  showErrorDetails?: boolean;
  showRetryButtons?: boolean;
  showHomeButtons?: boolean;
  
  // Form-specific settings
  formErrorBoundary?: {
    showResetButton?: boolean;
    showSaveButton?: boolean;
  };
  
  // Data-specific settings
  dataErrorBoundary?: {
    showRefreshButton?: boolean;
    showDownloadButton?: boolean;
  };
  
  // Route-specific settings
  routeErrorBoundary?: {
    showBackButton?: boolean;
  };
  
  // Custom error handlers
  onGlobalError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onFormError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onDataError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onRouteError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

// Default configuration
export const defaultErrorBoundaryConfig: ErrorBoundaryConfig = {
  enableGlobalErrorReporting: true,
  errorReportingEndpoint: import.meta.env.VITE_ERROR_REPORTING_ENDPOINT,
  showErrorDetails: import.meta.env.DEV,
  showRetryButtons: true,
  showHomeButtons: true,
  formErrorBoundary: {
    showResetButton: true,
    showSaveButton: false,
  },
  dataErrorBoundary: {
    showRefreshButton: true,
    showDownloadButton: false,
  },
  routeErrorBoundary: {
    showBackButton: true,
  },
};

// Context for error boundary configuration
export const ErrorBoundaryConfigContext = React.createContext<ErrorBoundaryConfig>(defaultErrorBoundaryConfig);

// Hook to use error boundary configuration
export const useErrorBoundaryConfig = () => {
  return React.useContext(ErrorBoundaryConfigContext);
};

// Provider component for error boundary configuration
export const ErrorBoundaryConfigProvider: React.FC<{
  children: React.ReactNode;
  config?: Partial<ErrorBoundaryConfig>;
}> = ({ children, config = {} }) => {
  const mergedConfig = React.useMemo(() => ({
    ...defaultErrorBoundaryConfig,
    ...config,
  }), [config]);

  return (
    <ErrorBoundaryConfigContext.Provider value={mergedConfig}>
      {children}
    </ErrorBoundaryConfigContext.Provider>
  );
};

// Pre-configured error boundary components
export const ConfiguredErrorBoundary: React.FC<{
  children: React.ReactNode;
  componentName?: string;
  fallback?: React.ReactNode;
  className?: string;
}> = ({ children, componentName, fallback, className }) => {
  const config = useErrorBoundaryConfig();
  
  return (
    <ErrorBoundary
      componentName={componentName}
      fallback={fallback}
      className={className}
      showRetry={config.showRetryButtons}
      showHomeButton={config.showHomeButtons}
      showErrorDetails={config.showErrorDetails}
      onError={config.onGlobalError}
    >
      {children}
    </ErrorBoundary>
  );
};

export const ConfiguredRouteErrorBoundary: React.FC<{
  children: React.ReactNode;
  routeName?: string;
  fallback?: React.ReactNode;
  className?: string;
}> = ({ children, routeName, fallback, className }) => {
  const config = useErrorBoundaryConfig();
  
  return (
    <RouteErrorBoundary
      routeName={routeName}
      fallback={fallback}
      className={className}
      showBackButton={config.routeErrorBoundary?.showBackButton}
      showHomeButton={config.showHomeButtons}
      onError={config.onRouteError}
    >
      {children}
    </RouteErrorBoundary>
  );
};

export { ErrorBoundaryConfigProvider as Provider, useErrorBoundaryConfig as useConfig };
